// Small shared subcomponents used by Videos page + sub-modals.
// Extracted from the monolithic Videos.jsx.
import React, { useEffect, useState } from "react";
import { Copy, Pencil } from "lucide-react";
import { toast } from "sonner";

export function PlatformRow({ label, color, statusOpts, status, onStatus, prices, postedAt, onPostedAt, testid }) {
  return (
    <div className="grid grid-cols-1 gap-2" data-testid={`${testid}-row`}>
      {/* Platform name row */}
      <div className="flex items-center gap-2 pb-1 border-b border-zinc-900">
        <span className="w-2 h-2 rounded-full" style={{ background: color }} />
        <span className="text-[13px] font-bold uppercase tracking-wider" style={{ color }}>{label}</span>
      </div>
      
      {/* Status, prices, date row */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center pl-4">
        <div className="md:col-span-4">
          <label className="text-[10px] uppercase tracking-wide block mb-1" style={{ color: "var(--gio-muted)" }}>Status</label>
          <select value={status || "none"} onChange={(e) => onStatus(e.target.value)}
            data-testid={`${testid}-status`}
            className="w-full bg-black border border-zinc-800 text-white text-[12px] font-mono-ui py-1.5 px-2 focus:outline-none focus:border-[color:var(--gio-primary)]">
            {statusOpts.map((o) => (<option key={o.v} value={o.v}>{o.label}</option>))}
          </select>
        </div>
        
        <div className="md:col-span-5 flex gap-1">
          {prices && prices.length > 0 && (
            <div className="flex-1">
              <label className="text-[10px] uppercase tracking-wide block mb-1" style={{ color: "var(--gio-muted)" }}>Pricing</label>
              <div className="flex gap-1">
                {prices.map((p, i) => (
                  <input key={i} type="number" min="0" step="0.01" value={p.value || ""}
                    onChange={(e) => p.onChange(parseFloat(e.target.value) || 0)}
                    placeholder={p.label}
                    data-testid={p.tid}
                    className="flex-1 bg-black border border-zinc-800 text-white text-[12px] font-mono-ui py-1.5 px-2 focus:outline-none focus:border-[color:var(--gio-primary)]" />
                ))}
              </div>
            </div>
          )}
          {(!prices || prices.length === 0) && (
            <div className="flex-1">
              <label className="text-[10px] uppercase tracking-wide block mb-1" style={{ color: "var(--gio-muted)" }}>Pricing</label>
              <div className="text-[10px] italic py-1.5" style={{ color: "var(--gio-muted-3)" }}>(no price)</div>
            </div>
          )}
        </div>
        
        <div className="md:col-span-3">
          <label className="text-[10px] uppercase tracking-wide block mb-1" style={{ color: "var(--gio-muted)" }}>📅 Posted Date</label>
          <input type="date" value={postedAt || ""}
            onChange={(e) => onPostedAt(e.target.value || null)}
            data-testid={`${testid}-posted`}
            title={`Date you posted on ${label}`}
            className="w-full bg-black border border-zinc-800 text-white text-[12px] font-mono-ui py-1.5 px-2 focus:outline-none focus:border-[color:var(--gio-primary)]" />
        </div>
      </div>
    </div>
  );
}

export function CrewChips({ label, icon: Icon, color, ids, actors, testid }) {
  const list = (ids || []).map((id) => actors.find((a) => a.id === id)).filter(Boolean);
  if (list.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 mt-1" data-testid={testid}>
      <span className="text-[9px] font-mono-ui font-bold inline-flex items-center gap-0.5"
        style={{ color }}>
        <Icon size={9} /> {label}
      </span>
      {list.slice(0, 2).map((a) => (
        <span key={a.id}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono-ui"
          style={{ background: `${color}15`, color, border: `1px solid ${color}40` }}>
          {a.photos?.[0] && <img src={a.photos[0]} alt="" className="w-3 h-3 rounded-full object-cover" />}
          {a.stage_name}
        </span>
      ))}
      {list.length > 2 && (
        <span className="text-[10px]" style={{ color: "var(--gio-muted-3)" }}>+{list.length - 2}</span>
      )}
    </div>
  );
}

export function PromoBlock({ title, text, tid, accent }) {
  return (
    <div className={`border p-4 ${accent ? "border-[color:var(--gio-secondary)] bg-[color:var(--gio-secondary)]/5" : "border-zinc-800 bg-black"}`} data-testid={tid}>
      <div className="flex items-center justify-between mb-2">
        <div className="overline">{title}</div>
        <button onClick={() => { navigator.clipboard.writeText(text); toast.success("Copied"); }} data-testid={`${tid}-copy`}
          className="text-zinc-400 hover:text-[color:var(--gio-primary)] text-xs font-mono-ui uppercase tracking-wider flex items-center gap-1">
          <Copy size={12} /> Copy
        </button>
      </div>
      <div className="text-white text-sm whitespace-pre-wrap leading-relaxed">{text}</div>
    </div>
  );
}

export function Sel({ value, options, onChange, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="bg-black border border-zinc-800 text-white text-xs font-mono-ui py-1.5 px-2 focus:outline-none focus:border-[color:var(--gio-primary)] min-w-[120px]">
      {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
    </select>
  );
}

export function StatusComment({ value, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(value);
  useEffect(() => { setLocal(value); }, [value]);
  const commit = () => {
    setEditing(false);
    const v = (local || "").trim();
    if (v !== (value || "")) onSave(v);
    else setLocal(value);
  };
  if (editing) {
    return (
      <input autoFocus value={local || ""} onChange={(e) => setLocal(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onBlur={commit}
        onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") commit(); if (e.key === "Escape") { setLocal(value); setEditing(false); } }}
        data-testid={testid}
        placeholder="🟡 Coming soon teaser · Edit BCN/MAD nights · 2nd in Viktor Rom Collection"
        className="w-full mt-2 px-2 py-1 text-[11px] rounded outline-none border"
        style={{ background: "rgba(251,191,36,0.08)", borderColor: "#fbbf24", color: "#fbbf24" }} />
    );
  }
  if (!value) {
    return (
      <button onClick={(e) => { e.stopPropagation(); setEditing(true); }} type="button" data-testid={testid}
        className="mt-2 inline-flex items-center gap-1 text-[10px] opacity-50 hover:opacity-100 transition"
        style={{ color: "#fbbf24" }}>
        <Pencil size={9} /> add status note
      </button>
    );
  }
  return (
    <button onClick={(e) => { e.stopPropagation(); setEditing(true); }} type="button" data-testid={testid}
      className="group w-full mt-2 px-2 py-1 text-left text-[11px] rounded border transition hover:border-amber-400"
      style={{ background: "rgba(251,191,36,0.08)", borderColor: "rgba(251,191,36,0.35)", color: "#fbbf24" }}>
      <span>{value}</span>
      <Pencil size={9} className="opacity-0 group-hover:opacity-60 inline ml-1.5 align-middle" />
    </button>
  );
}

export function InlineEdit({ value, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(value);
  useEffect(() => { setLocal(value); }, [value]);
  const commit = () => {
    setEditing(false);
    const v = (local || "").trim();
    if (v && v !== value) onSave(v);
    else setLocal(value);
  };
  if (editing) {
    return (
      <input autoFocus value={local} onChange={(e) => setLocal(e.target.value)}
        onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setLocal(value); setEditing(false); } }}
        data-testid={testid}
        className="gio-input w-full !py-1.5 !text-[14px]" />
    );
  }
  return (
    <button onClick={(e) => { e.stopPropagation(); setEditing(true); }} data-testid={testid}
      className="group font-medium text-left hover:text-[color:var(--gio-primary)] transition inline-flex items-start gap-1.5" style={{ color: "var(--gio-text)" }}>
      <span>{value}</span>
      <Pencil size={11} className="opacity-0 group-hover:opacity-60 mt-1 shrink-0" />
    </button>
  );
}

export function Num({ value, onChange, testid, placeholder }) {
  const [local, setLocal] = useState(value ?? 0);
  useEffect(() => { setLocal(value ?? 0); }, [value]);
  return (
    <input
      type="number" step="0.01"
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => { const v = parseFloat(local) || 0; if (v !== (value ?? 0)) onChange(v); }}
      placeholder={placeholder}
      data-testid={testid}
      className="w-16 bg-black border border-zinc-800 text-white text-xs font-mono-ui py-1.5 px-2 focus:outline-none focus:border-[color:var(--gio-primary)]"
    />
  );
}

export function Stat({ label, value, testid, accent }) {
  return (
    <div 
      className={`rounded-xl border p-4 transition-all hover:scale-105 ${
        accent 
          ? "border-pink-500/40 bg-gradient-to-br from-purple-900/30 to-pink-900/30 shadow-lg shadow-pink-500/20" 
          : "border-zinc-800 bg-black/40 backdrop-blur-sm hover:border-purple-500/30"
      }`} 
      data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-1">
        {label}
      </div>
      <div className={`font-heading text-3xl font-bold ${
        accent ? "bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent" : "text-white"
      }`}>
        {value}
      </div>
    </div>
  );
}
